import cn from 'classnames'
import { useResolvedDesignSystemComponent } from 'ext-ui/design-system/DesignSystemChannel'
import GeneratedLatestAlertPanel, {
  AlertPanelProps as GeneratedAlertPanelProps,
} from 'ext-ui/design-system/generated/latest/components/AlertPanel/AlertPanel'
import GeneratedLiveAlertPanel from 'ext-ui/design-system/generated/live/components/AlertPanel/AlertPanel'
import React, { ComponentProps, ReactElement, ReactNode } from 'react'

export type AlertPanelProps = {
  className?: string
  icon?: ReactElement
  variant?:
    | 'default'
    | 'default-wcag'
    | 'severe'
    | 'severe-wcag'
    | 'warning'
    | 'warning-wcag'
    | 'automation'
    | 'automation-wcag'
  children: ReactNode
  title?: string
} & Omit<ComponentProps<'div'>, 'children' | 'title'>

type PublicAlertPanelVariant = NonNullable<AlertPanelProps['variant']>
type PublicAlertPanelBaseVariant =
  | 'default'
  | 'severe'
  | 'warning'
  | 'automation'
type GeneratedAlertPanelVariant = NonNullable<
  GeneratedAlertPanelProps['variant']
>

const generatedVariantByPublicVariant: Record<
  PublicAlertPanelBaseVariant,
  GeneratedAlertPanelVariant
> = {
  automation: 'ai',
  default: 'soft',
  severe: 'severe',
  warning: 'warning',
}

function getVariantParts(variant: PublicAlertPanelVariant): {
  baseVariant: PublicAlertPanelBaseVariant
  wcag: boolean
} {
  const [baseVariant, suffix] = variant.split('-') as [
    PublicAlertPanelBaseVariant,
    string | undefined,
  ]

  return {
    baseVariant,
    wcag: suffix === 'wcag',
  }
}

function renderDesignSystemAlertPanel(
  Component: typeof GeneratedLiveAlertPanel,
  channel: 'live' | 'latest',
  props: AlertPanelProps,
): ReactElement {
  const {
    className = '',
    icon,
    variant = 'default',
    children,
    title: nativeTitle,
    ...rest
  } = props
  const { baseVariant, wcag } = getVariantParts(variant)

  const panel = (
    <Component
      {...rest}
      className={cn(className, baseVariant, { wcag })}
      data-design-system-channel={channel}
      icon={icon}
      subtitle={children}
      textStyle={wcag ? 'monochrome' : 'color'}
      variant={generatedVariantByPublicVariant[baseVariant]}
    />
  )

  return nativeTitle === undefined ? (
    panel
  ) : (
    <div title={nativeTitle}>{panel}</div>
  )
}

function LiveAlertPanel(props: AlertPanelProps): ReactElement {
  return renderDesignSystemAlertPanel(GeneratedLiveAlertPanel, 'live', props)
}

function LatestAlertPanel(props: AlertPanelProps): ReactElement {
  return renderDesignSystemAlertPanel(
    GeneratedLatestAlertPanel,
    'latest',
    props,
  )
}

function AlertPanel(props: AlertPanelProps): ReactElement {
  const ResolvedAlertPanel = useResolvedDesignSystemComponent(
    LiveAlertPanel,
    LatestAlertPanel,
  )

  return <ResolvedAlertPanel {...props} />
}

export default AlertPanel
